import type {
  AriaLabelingProps,
  DOMAttributes,
  DOMProps,
} from '@react-types/shared';
import type {GridCollection} from '@react-types/grid';
import type {Key, RefObject} from 'react';
import {useCallback, useMemo} from 'react';
import {filterDOMProps, mergeProps, useId} from '../utils';
import {useHasTabbableChild} from '../focus';
import {useCollator, useLocale} from '../i18n';
import type {GridState} from '../../@react-stately/grid';
import {useSelectableCollection} from '../selection';
import {useGridSelectionAnnouncement} from './useGridSelectionAnnouncement';
import {useHighlightSelectionDescription} from './useHighlightSelectionDescription';
import {gridMap} from './utils';
import {GridKeyboardDelegate} from './GridKeyboardDelegate';
import type {KeyboardDelegate} from './types';

export interface GridProps extends DOMProps, AriaLabelingProps {
  /** Whether the grid uses virtual scrolling. */
  isVirtualized?: boolean;
  /**
   * An optional keyboard delegate implementation for type to select,
   * to override the default.
   */
  keyboardDelegate?: KeyboardDelegate;
  /**
   * Whether initial grid focus should be placed on the grid row or grid cell.
   * @default 'row'
   */
  focusMode?: 'row' | 'cell';
  /**
   * A function that returns the text that should be announced by assistive technology when a row is added or removed from selection.
   * @default (key) => state.collection.getItem(key)?.textValue
   */
  getRowText?: (key: Key) => string;
  /**
   * The ref attached to the scrollable body. Used to provided automatic scrolling on item focus for non-virtualized grids.
   */
  scrollRef?: RefObject<HTMLElement>;
  /** Handler that is called when a user performs an action on the row. */
  onRowAction?: (key: Key) => void;
  /** Handler that is called when a user performs an action on the cell. */
  onCellAction?: (key: Key) => void;
}

export interface GridAria {
  /** Props for the grid element. */
  gridProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a grid component.
 * A grid displays data in one or more rows and columns and enables a user to navigate its contents via directional navigation keys.
 * @param props - Props for the grid.
 * @param state - State for the grid, as returned by `useGridState`.
 * @param ref - The ref attached to the grid element.
 */
export function useGrid<T>(
  props: GridProps,
  state: GridState<T, GridCollection<T>>,
  ref: RefObject<HTMLElement>,
): GridAria {
  const {
    isVirtualized,
    keyboardDelegate,
    focusMode,
    scrollRef,
    getRowText,
    onRowAction,
    onCellAction,
  } = props;
  const {selectionManager: manager} = state;

  if (!props['aria-label'] && !props['aria-labelledby']) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        'An aria-label or aria-labelledby prop is required for accessibility.',
      );
    }
  }

  // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.
  const collator = useCollator({usage: 'search', sensitivity: 'base'});
  const {direction} = useLocale();
  const disabledBehavior = state.selectionManager.disabledBehavior;
  const delegate = useMemo(
    () =>
      keyboardDelegate ||
      new GridKeyboardDelegate({
        collection: state.collection,
        disabledKeys:
          disabledBehavior === 'selection' ? new Set() : state.disabledKeys,
        ref,
        direction,
        collator,
        focusMode,
      }),
    [
      keyboardDelegate,
      state.collection,
      state.disabledKeys,
      disabledBehavior,
      ref,
      direction,
      collator,
      focusMode,
    ],
  );

  const {collectionProps} = useSelectableCollection({
    ref,
    selectionManager: manager,
    // @ts-expect-error Type 'GridKeyboardDelegate<unknown, GridCollection<T>>' is not assignable to type 'KeyboardDelegate'
    keyboardDelegate: delegate,
    isVirtualized,
    scrollRef,
  });

  const id = useId(props.id);
  gridMap.set(state, {
    // @ts-expect-error Type 'GridKeyboardDelegate<unknown, GridCollection<T>>' is not assignable to type 'KeyboardDelegate'
    keyboardDelegate: delegate,
    actions: {onRowAction, onCellAction},
  });

  const descriptionProps = useHighlightSelectionDescription({
    selectionManager: manager,
    hasItemActions: !!(onRowAction || onCellAction),
  });

  const domProps = filterDOMProps(props, {labelable: true});

  const onFocus = useCallback(
    (e) => {
      if (manager.isFocused) {
        // If a focus event bubbled through a portal, reset focus state.
        if (!e.currentTarget.contains(e.target)) {
          manager.setFocused(false);
        }

        return;
      }

      // Focus events can bubble through portals. Ignore these events.
      if (!e.currentTarget.contains(e.target)) {
        return;
      }

      manager.setFocused(true);
    },
    [manager],
  );

  // Continue to track collection focused state even if keyboard navigation is disabled
  const navDisabledHandlers = useMemo(
    () => ({
      onBlur: collectionProps.onBlur,
      onFocus,
    }),
    [onFocus, collectionProps.onBlur],
  );

  const hasTabbableChild = useHasTabbableChild(ref, {
    isDisabled: state.collection.size !== 0,
  });

  const gridProps: DOMAttributes = mergeProps(
    domProps,
    {
      role: 'grid',
      id,
      'aria-multiselectable':
        manager.selectionMode === 'multiple' ? 'true' : undefined,
    },
    state.isKeyboardNavigationDisabled ? navDisabledHandlers : collectionProps,
    // If collection is empty, make sure the grid is tabbable unless there is a child tabbable element.
    state.collection.size === 0 ? {tabIndex: hasTabbableChild ? -1 : 0} : {},
    descriptionProps,
  );

  if (isVirtualized) {
    gridProps['aria-rowcount'] = state.collection.size;
    gridProps['aria-colcount'] = state.collection.columnCount;
  }

  useGridSelectionAnnouncement({getRowText}, state);
  return {
    gridProps,
  };
}
